"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui/sonner";
import {
  createScorecardFill,
  getListScorecardFillsQueryKey,
  listScorecardFills,
  updateScorecardFill,
} from "@/lib/api/generated";
import type { ScorecardFillCreate, ScorecardFillPatch } from "@/lib/api/model";

import { apiErrorCode, apiErrorMessage } from "./use-definitions";

export function useScorecardFills(jobId: string, jcId: string, definitionId?: string) {
  const params = definitionId ? { scorecard_definition_id: definitionId } : undefined;
  return useQuery({
    queryKey: getListScorecardFillsQueryKey(jobId, jcId, params),
    queryFn: () => listScorecardFills(jobId, jcId, params),
    enabled: Boolean(jobId && jcId),
    refetchOnWindowFocus: false, // every list is audited (decision 163g); one fetch per open
  });
}

function useInvalidateFills(jobId: string, jcId: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: getListScorecardFillsQueryKey(jobId, jcId) });
}

export function useCreateFill(jobId: string, jcId: string) {
  const invalidate = useInvalidateFills(jobId, jcId);
  return useMutation({
    mutationFn: (body: ScorecardFillCreate) => createScorecardFill(jobId, jcId, body),
    onSuccess: (_d, vars) => {
      invalidate();
      showToast.success(vars.submit ? "Scorecard submitted" : "Draft saved");
    },
    // 409 SCORECARD_FILL_EXISTS is handled by the form (offers "Open existing"); other errors toast.
    onError: (err) => {
      if (apiErrorCode(err) !== "SCORECARD_FILL_EXISTS") {
        showToast.error("Could not save scorecard", { description: apiErrorMessage(err) });
      }
    },
  });
}

export function useUpdateFill(jobId: string, jcId: string) {
  const invalidate = useInvalidateFills(jobId, jcId);
  return useMutation({
    mutationFn: ({ fillId, body }: { fillId: string; body: ScorecardFillPatch }) =>
      updateScorecardFill(jobId, jcId, fillId, body),
    onSuccess: (_d, vars) => {
      invalidate();
      showToast.success(vars.body.submit ? "Scorecard submitted" : "Saved");
    },
    onError: (err) =>
      showToast.error("Could not save scorecard", { description: apiErrorMessage(err) }),
  });
}
